import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import EmptyTable from '../../common/util/EmptyTable'
import { get } from '../../helpers/Api'
import StatsElement from '../StatsElement'
import UsersTableElement from './UsersTableElement'

export const UsersTable = () => {
    const [chartRange, setChartRange] = useState(1)
    const [isLoading, setIsLoading] = useState(true)
    const [loadedDataPages, setLoadedDataPages] = useState(0)
    const [data, setData] = useState(null)
    const [page, setPage] = useState(0)

    const handlePageChange = (p) => {
        setPage(p)
    }

    const [userType, setUserType] = useState('all')

    const { t } = useTranslation('common')

    const selectUserType = (type) => {
        setIsLoading(true)
        setUserType(type)
    }

    const chartDropDownOptions = [
        { value: 1, label: t('analytics.24h_market_volume') },
        { value: 2, label: t('analytics.days_market_volume', { days: 2 }) },
        { value: 3, label: t('analytics.days_market_volume', { days: 3 }) },
        { value: 7, label: t('analytics.days_market_volume', { days: 7 }) },
        { value: 15, label: t('analytics.days_market_volume', { days: 15 }) },
        { value: 30, label: t('analytics.days_market_volume', { days: 30 }) },
        { value: 60, label: t('analytics.days_market_volume', { days: 60 }) },
        { value: 90, label: t('analytics.days_market_volume', { days: 90 }) },
        { value: 180, label: t('analytics.days_market_volume', { days: 180 }) },
        { value: 365, label: t('analytics.days_market_volume', { days: 365 }) },
        { value: 0, label: t('analytics.all_time_market_volume') },
    ]

    const onSelectChartRange = (e) => {
        if (e.target.value !== chartRange) setChartRange(e.target.value)
    }

    const ROWS_PER_PAGE = 20

    const getData = async (loadedPages, range, refresh = false) => {
        if (refresh) {
            setLoadedDataPages(0)
        }

        let url = `top-users/${range}`

        url += `?limit=${ROWS_PER_PAGE}&offset=${
            refresh ? 0 : loadedDataPages * ROWS_PER_PAGE
        }`

        if (userType !== 'all') {
            url += `&actor=${userType}`
        }

        return await get(url, 'v3')
    }

    useEffect(() => {
        setIsLoading(true)
        getData(loadedDataPages, chartRange, true).then((res) => parseData(res))
    }, [chartRange, userType])

    useEffect(() => {
        if (page >= loadedDataPages) {
            setIsLoading(true)
            getData(loadedDataPages, chartRange, false).then((res) =>
                parseData(res, true),
            )
        }
    }, [page])

    const parseData = (res, append = false) => {
        setData(append && data ? data.concat(res) : res)
        setLoadedDataPages(append ? loadedDataPages + 1 : 1)
        setIsLoading(false)
    }

    return (
        <div className="h-auto w-auto overflow-y-auto dark:text-white text-black">
            <StatsElement
                title={''}
                chartRange={chartRange}
                onChange={onSelectChartRange}
                chartDropDownOptions={chartDropDownOptions}
                selectUserType={selectUserType}
                userType={userType}
            >
                <div className="relative w-full mb-auto">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Rank</TableCell>
                                <TableCell>User</TableCell>
                                {['all'].includes(userType) && (
                                    <TableCell>Volume</TableCell>
                                )}
                                {['all'].includes(userType) && (
                                    <TableCell>Buy Volume</TableCell>
                                )}
                                {['buyer'].includes(userType) && (
                                    <TableCell>Buy Volume (WAX)</TableCell>
                                )}
                                {['buyer'].includes(userType) && (
                                    <TableCell>Buy Volume (USD)</TableCell>
                                )}
                                {['all'].includes(userType) && (
                                    <TableCell>Sell Volume</TableCell>
                                )}
                                {['seller'].includes(userType) && (
                                    <TableCell>Sell Volume (WAX)</TableCell>
                                )}
                                {['seller'].includes(userType) && (
                                    <TableCell>Sell Volume (USD)</TableCell>
                                )}
                                {['buyer', 'all'].includes(userType) && (
                                    <TableCell>Purchases</TableCell>
                                )}
                                {['seller', 'all'].includes(userType) && (
                                    <TableCell>Sales</TableCell>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                <EmptyTable
                                    cols={
                                        userType === 'all'
                                            ? 7
                                            : ['buyer', 'seller'].includes(
                                                  userType,
                                              )
                                            ? 5
                                            : 5
                                    }
                                    rows={ROWS_PER_PAGE}
                                    isLoading={isLoading}
                                />
                            ) : (
                                <>
                                    {data &&
                                        data
                                            .slice(
                                                page * ROWS_PER_PAGE,
                                                page * ROWS_PER_PAGE +
                                                    ROWS_PER_PAGE,
                                            )
                                            .map((user, index) => (
                                                <UsersTableElement
                                                    user={user}
                                                    index={index}
                                                    userType={userType}
                                                />
                                            ))}
                                    <EmptyTable
                                        cols={
                                            userType === 'all'
                                                ? 7
                                                : ['buyer', 'seller'].includes(
                                                      userType,
                                                  )
                                                ? 5
                                                : 5
                                        }
                                        rows={
                                            ROWS_PER_PAGE -
                                            (data
                                                ? data.slice(
                                                      page * ROWS_PER_PAGE,
                                                      page * ROWS_PER_PAGE +
                                                          ROWS_PER_PAGE,
                                                  ).length
                                                : 0)
                                        }
                                    />
                                </>
                            )}
                        </TableBody>
                        <TablePagination
                            // This is to display the 'x-y of z' at the bottom with the pagination. When doing a search
                            // with a searchterm, we have the total number of results, as we aren't limiting
                            // it. In that case we can pass in the total count. For searches without a search term we don't
                            // know the total amount of results, so we pass in -1
                            count={data && data.length > 0 && data[0].total}
                            labelRowsPerPage={'Rows per page:'}
                            onPageChange={(e, page) => handlePageChange(page)}
                            page={page}
                            rowsPerPage={ROWS_PER_PAGE}
                            rowsPerPageOptions={-1}
                            showFirstButton
                            nextIconButtonProps={{ disabled: isLoading }}
                            sx={{ borderBottom: 0 }}
                        />
                    </Table>
                </div>
            </StatsElement>
        </div>
    )
}
